import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clapperboard,
  Lightbulb,
  ListTodo,
  PlayCircle,
  Settings2,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiClient } from '@/lib/api/client'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { UserRole } from '@/types/entities'
import { ArtworkPreview, StudioKpi } from '@/features/films/StudioComponents'
import { FilmStudioPage } from '@/features/films/FilmStudioPage'
import { WeeklyProgrammePanel } from '@/features/films/WeeklyProgrammePanel'
import { FilmSocialPanel } from '@/features/films/FilmSocialPanel'
import { WorldLibraryPage } from '@/features/worlds/WorldLibraryPage'
import { SelectArtworkDialog } from './SelectArtworkDialog'
import { ReleasePanel } from './ReleasePanel'
import { ProducerContractPanel } from './ProducerContractPanel'

type Scope = { artistId: string; artworkId: string; experienceId: string }
export type Run = {
  kind: 'artwork' | 'film'
  id: string
  programme_id?: string
  title: string
  artist: string
  source_image_url?: string
  stage: string
  bucket: string
  scope?: Scope
  film_job_id?: string
  film_status?: string
  updated_at: string
  managed_mode?: boolean
}
type Workflow = {
  id: string
  discoveryEnabled: boolean
  creationEnabled: boolean
  creationPilotLimited?: boolean
  immersityPricingConfigured?: boolean
  costContractConfigured: boolean
  publicationEnabled: boolean
  durationSec: number
  weeklyReservationCapCents: number
}
type Board = {
  items: Run[]
  total: number
  limit: number
  offset: number
  summary: Record<string, number>
  workflow: Workflow
}
const root = '/app/content-studio'
const views = [
  ['runs', 'Runs', ListTodo],
  ['ideas', 'Ideas', Lightbulb],
  ['worlds', 'Worlds', PlayCircle],
  ['films', 'Films', Clapperboard],
  ['schedule', 'Scheduling', CalendarDays],
  ['accounts', 'Connections', ShieldCheck],
  ['workflow', 'Workflow', Settings2],
] as const
export function runPresentation(run: Run, enabled: boolean) {
  const filmHref = `${root}?view=films&job=${encodeURIComponent(run.film_job_id || run.id)}`
  const worldHref = run.scope
    ? `${root}?view=worlds&world=${encodeURIComponent([run.scope.artistId, run.scope.artworkId, run.scope.experienceId].join('/'))}`
    : `${root}?view=worlds`
  if (run.bucket === 'ideas')
    return {
      label: 'Idea · not queued',
      detail:
        'Select concepts and approve creation once. Original preparation and grounding run on the server.',
      action: 'Choose concepts',
      href: `${root}?view=ideas&programme=${encodeURIComponent(run.programme_id || '')}`,
      phase: 0,
    }
  if (run.bucket === 'waiting')
    return {
      label: enabled ? 'Approved · waiting for runner' : 'Approved · production paused',
      detail: enabled
        ? 'Creation is approved, but this item is waiting for runner capacity or pilot activation. No additional source review is required in managed mode.'
        : 'Creation approval is saved, but the fresh-world runner is off. This is not a queued vendor job.',
      action: 'View workflow',
      href: `${root}?view=workflow`,
      phase: 1,
    }
  if (run.stage === 'world_ready')
    return {
      label: 'World complete',
      detail:
        'Preview on desktop or phone. Choose Create film only if you want a film from this world.',
      action: 'Open world library',
      href: `${root}?view=worlds`,
      phase: 2,
    }
  if (run.stage === 'world_review' && run.managed_mode)
    return {
      label: 'Film requested',
      detail: 'The selected world is being prepared for automatic film direction and rendering.',
      action: 'Open world library',
      href: `${root}?view=worlds`,
      phase: 3,
    }
  if (run.stage === 'world_review')
    return {
      label: 'Your review · world',
      detail: 'Inspect the built scene. Approve the exact world to let the runner create its film.',
      action: 'Review world',
      href: worldHref,
      phase: 2,
    }
  if (run.bucket === 'ready')
    return {
      label: 'Film approved · not posted',
      detail:
        'Film review is complete. Choose the connected account, placement and posting time in Schedules; release approval is separate.',
      action: 'Open approved film',
      href: filmHref,
      phase: 4,
    }
  if (run.bucket === 'review')
    return {
      label: 'Your review · film',
      detail:
        'Watch the encoded film and record approval or a correction. Approval is not publication.',
      action: 'Review film',
      href: filmHref,
      phase: 3,
    }
  if (run.bucket === 'issues')
    return {
      label: 'Needs attention',
      detail:
        'Production requires a correction or reconciliation. No paid retry is started by opening this item.',
      action: run.film_job_id ? 'Inspect film' : run.scope ? 'Inspect world' : 'Review source',
      href: run.film_job_id
        ? filmHref
        : run.scope
          ? worldHref
          : `${root}?view=ideas&programme=${encodeURIComponent(run.programme_id || '')}`,
      phase: run.film_job_id ? 3 : 1,
    }
  if (run.bucket === 'archived')
    return {
      label: run.stage === 'rejected' ? 'Artwork declined' : 'Run cancelled',
      detail: 'This item will not advance. Opening it does not restart production.',
      action: 'View details',
      href: run.film_job_id
        ? filmHref
        : run.scope
          ? worldHref
          : `${root}?view=ideas&programme=${encodeURIComponent(run.programme_id || '')}`,
      phase: 0,
    }
  const labels: Record<string, string> = {
    source: 'Preparing source',
    world: 'Building world',
    world_assets: 'Finishing Immersity & AR assets',
    film_plan: 'Directing film',
    render: 'Rendering film',
    film_critique: 'Critiquing encoded film',
    queued: 'Film render queued',
    running: 'Film rendering',
  }
  return {
    label: labels[run.stage] || 'Status unavailable',
    detail:
      run.kind === 'artwork' && !enabled
        ? 'A durable run exists, but orchestration is paused. Downstream work already dispatched may finish; review the workflow status.'
        : 'Production is in progress. The next human review appears here automatically.',
    action:
      run.scope && !run.film_job_id
        ? 'Inspect world'
        : run.film_job_id
          ? 'View film run'
          : 'View workflow',
    href:
      run.scope && !run.film_job_id
        ? worldHref
        : run.film_job_id
          ? filmHref
          : `${root}?view=workflow`,
    phase: ['source', 'world'].includes(run.stage) ? 1 : 3,
  }
}
// Old bookmarks and OAuth returns stay usable; all query parameters survive the manager redirect.
export function LegacyStudioRoute({
  from,
  children,
}: {
  from: 'films' | 'worlds' | 'accounts'
  children: React.ReactNode
}) {
  const manager = useAuthStore((s) => s.user?.role === UserRole.MANAGER)
  const [params] = useSearchParams()
  if (!manager) return <>{children}</>
  const next = new URLSearchParams(params)
  const oldView = params.get('view') || ''
  next.set(
    'view',
    from === 'worlds'
      ? 'worlds'
      : from === 'accounts' || params.has('grant') || params.has('social')
        ? 'accounts'
        : oldView === 'candidates'
          ? 'ideas'
          : oldView === 'schedules'
            ? 'schedule'
            : 'films'
  )
  return <Navigate replace to={`${root}?${next}`} />
}
function nextWeek() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value)
  const date = new Date(Date.UTC(get('year'), get('month') - 1, get('day')))
  date.setUTCDate(date.getUTCDate() + ((8 - date.getUTCDay()) % 7 || 7))
  return date.toISOString().slice(0, 10)
}
export function ContentStudioPage() {
  const queryClient = useQueryClient()
  const manager = useAuthStore((s) => s.user?.role === UserRole.MANAGER)
  const [params, setParams] = useSearchParams()
  const requested =
    params.has('grant') || params.has('social') ? 'accounts' : params.get('view') || 'runs'
  const view = views.some((v) => v[0] === requested) ? requested : 'runs'
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [bucket, setBucket] = useState('')
  const [offset, setOffset] = useState(0)
  const [discoverOpen, setDiscoverOpen] = useState(false)
  const [selectOpen, setSelectOpen] = useState(() => /^\d{1,10}$/.test(params.get('artwork') || ''))
  const [week, setWeek] = useState(nextWeek)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [cancelRun, setCancelRun] = useState<Run | null>(null)
  const confirmed = useRef(false)
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(search.trim())
      setOffset(0)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [search])
  const board = useQuery({
    queryKey: ['content-studio', query, bucket, offset],
    queryFn: async () =>
      (
        await apiClient.get<Board>('/api/v1/worlds/studio', {
          params: { q: query, bucket, offset },
        })
      ).data,
    enabled: manager,
    retry: false,
    refetchInterval: 15000,
    placeholderData: (previous) => previous,
  })
  const data = board.data
  const workflow = data?.workflow
  if (!manager)
    return (
      <div className="p-6">
        <h1 className="text-2xl">Content Studio</h1>
        <p className="mt-3 text-sm">
          This production workspace is available to administrators. Artist connection tools retain
          their existing rollout gates.
        </p>
      </div>
    )
  return (
    <main className="mx-auto max-w-7xl space-y-6 p-4 font-sans md:p-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Arthaus · creative production
          </p>
          <h1 className="mt-1 text-3xl font-semibold">Content Studio</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            One place to find artworks, follow production runs, review worlds and films, and prepare
            their release.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="min-h-11" onClick={() => setSelectOpen(true)}>
            Choose artwork
          </Button>
          <Button
            className="min-h-11"
            disabled={!workflow || busy}
            onClick={() => {
              confirmed.current = false
              setMessage('')
              setDiscoverOpen(true)
            }}
          >
            <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
            Plan a week
          </Button>
        </div>
      </header>
      <ol
        aria-label="Production journey"
        className="grid grid-cols-2 gap-2 rounded-2xl border bg-card p-3 lg:grid-cols-5"
      >
        {[
          'Select & approve concepts',
          'Automatic world build',
          'World library & preview',
          'Optional film & review',
          'Schedule release',
        ].map((label, i) => (
          <li key={label} className="flex items-center gap-2 rounded-lg bg-muted/40 p-3 text-xs">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border bg-background font-semibold">
              {i + 1}
            </span>
            {label}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-muted/30 px-4 py-3 text-sm">
        <div className="min-w-0">
          <span className="font-medium">Launch mode: human-reviewed</span>
          <span className="text-muted-foreground">
            {' '}
            ·{' '}
            {workflow
              ? workflow.creationEnabled
                ? workflow.creationPilotLimited
                  ? 'Fresh-world runner enabled for the selected pilot only'
                  : 'Fresh-world runner enabled'
                : 'Fresh-world production paused'
              : 'Checking runner status…'}{' '}
            · Only explicitly approved releases can post
          </span>
        </div>
        <Link
          className="inline-flex shrink-0 items-center gap-1 text-sm underline"
          to={`${root}?view=workflow`}
        >
          Workflow & blockers <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>
      <nav aria-label="Content Studio sections" className="flex flex-wrap gap-2 border-b pb-3">
        {views.map(([id, label, Icon]) => (
          <Button
            key={id}
            variant={view === id ? 'default' : 'outline'}
            className="min-h-11"
            aria-current={view === id ? 'page' : undefined}
            onClick={() => setParams({ view: id })}
          >
            <Icon className="mr-2 h-4 w-4" aria-hidden="true" />
            {label}
          </Button>
        ))}
      </nav>
      {(message || board.error) && (
        <div role="status" className="rounded-xl border bg-card p-4 text-sm">
          {message ||
            'Production overview unavailable. Counts and runner status are not known; review tools remain available. Retry loading before starting a run.'}
          {board.error && (
            <Button variant="outline" className="ml-3" onClick={() => void board.refetch()}>
              Retry overview
            </Button>
          )}
        </div>
      )}
      {view === 'runs' && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StudioKpi
              label="Ideas to review"
              value={data?.summary.ideas}
              note="Not queued or generated"
            />
            <StudioKpi
              label="Waiting for production"
              value={data?.summary.waiting}
              note={
                workflow?.creationEnabled
                  ? 'Approved sources awaiting runner'
                  : 'Approved sources; runner paused'
              }
            />
            <StudioKpi
              label="Production in progress"
              value={data?.summary.active}
              note="Durable world/film runs"
            />
            <StudioKpi
              label="Your reviews"
              value={data?.summary.review}
              note="Worlds and encoded films"
            />
          </div>
          <section className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">Production board</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Ideas are not jobs. Each row shows its actual stage and the next action; linked
                  worlds and films count once.
                </p>
              </div>
              <p className="text-xs text-muted-foreground" aria-live="polite">
                {board.isFetching ? 'Refreshing…' : `${data?.total ?? '—'} matching items`}
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="min-w-0 flex-1 text-sm">
                Search production
                <input
                  aria-label="Search production"
                  className="mt-1 min-h-11 w-full rounded-lg border bg-background px-3"
                  placeholder="Artwork, artist or run ID…"
                  type="search"
                  maxLength={120}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
              <label className="text-sm">
                Stage
                <select
                  aria-label="Production stage"
                  className="mt-1 min-h-11 max-w-full rounded-lg border bg-background px-3"
                  value={bucket}
                  onChange={(e) => {
                    setBucket(e.target.value)
                    setOffset(0)
                  }}
                >
                  <option value="">All stages</option>
                  {[
                    ['ideas', 'Ideas · not queued'],
                    ['waiting', 'Approved · waiting'],
                    ['active', 'In progress'],
                    ['worlds', 'World complete'],
                    ['review', 'Your review'],
                    ['ready', 'Film approved'],
                    ['issues', 'Needs attention'],
                    ['archived', 'Declined / cancelled'],
                  ].map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {data && !data.items.length && (
              <div className="rounded-2xl border border-dashed p-8">
                <h3 className="font-semibold">
                  {query || bucket
                    ? 'No items match these filters'
                    : 'Start with a week of artwork ideas'}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Plan a week to discover source-grounded concepts. Approve creation once; the
                  worker checks originals and grounding within the configured spend allowance.
                </p>
              </div>
            )}
            <div className="space-y-3">
              {data?.items.map((run) => {
                const p = runPresentation(run, !!workflow?.creationEnabled)
                return (
                  <article
                    key={`${run.kind}-${run.id}`}
                    className="grid min-w-0 gap-4 rounded-2xl border bg-card p-4 sm:grid-cols-[140px_1fr] lg:grid-cols-[140px_1fr_240px]"
                    aria-label={`Production ${run.title}`}
                  >
                    <ArtworkPreview
                      title={run.title}
                      sourceUrl={run.source_image_url}
                      jobId={run.kind === 'film' ? run.film_job_id : undefined}
                    />
                    <div className="min-w-0 space-y-2">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${run.bucket === 'review' || run.bucket === 'waiting' ? 'bg-amber-100 text-amber-900' : run.bucket === 'ready' ? 'bg-emerald-100 text-emerald-900' : run.bucket === 'issues' ? 'bg-red-100 text-red-900' : 'bg-muted text-muted-foreground'}`}
                      >
                        {p.label}
                      </span>
                      <h3 className="break-words text-lg font-semibold">{run.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {run.artist || 'Artist not recorded'} ·{' '}
                        {run.kind === 'film' ? 'Existing-world film' : 'Artwork → world → film'}
                      </p>
                      <p className="text-sm leading-relaxed text-muted-foreground">{p.detail}</p>
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer">Run details</summary>
                        <p className="mt-2 break-all">
                          {run.stage} · {run.id}
                          <br />
                          Updated {new Date(run.updated_at).toLocaleString()}
                        </p>
                        {run.kind === 'artwork' &&
                          [
                            'source',
                            'world',
                            'world_review',
                            'film_plan',
                            'render',
                            'film_critique',
                            'revision_required',
                            'reapproval_required',
                          ].includes(run.stage) && (
                            <Button
                              variant="outline"
                              className="mt-3 min-h-11"
                              onClick={() => {
                                setMessage('')
                                setCancelRun(run)
                              }}
                            >
                              Cancel production run
                            </Button>
                          )}
                      </details>
                    </div>
                    <div className="flex flex-col justify-center gap-3">
                      <Button asChild className="min-h-11">
                        <Link to={p.href}>
                          {p.action}
                          <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                        </Link>
                      </Button>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        Step {p.phase + 1} of 5 · No automatic release
                      </p>
                    </div>
                  </article>
                )
              })}
            </div>
            {!!data?.total && (
              <div className="flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  disabled={!offset || board.isFetching}
                  onClick={() => setOffset(Math.max(0, offset - (data?.limit || 24)))}
                >
                  Previous
                </Button>
                <span className="text-xs text-muted-foreground">
                  {offset + 1}–{Math.min(offset + data.items.length, data.total)} of {data.total}
                </span>
                <Button
                  variant="outline"
                  disabled={board.isFetching || offset + data.items.length >= data.total}
                  onClick={() => setOffset(offset + data.limit)}
                >
                  Next
                </Button>
              </div>
            )}
          </section>
        </>
      )}
      <SelectArtworkDialog
        key={params.get('artwork') || 'selection'}
        open={selectOpen}
        onClose={() => setSelectOpen(false)}
        initialId={params.get('artwork') || undefined}
        defaultWeek={nextWeek()}
      />
      {view === 'ideas' && (
        <WeeklyProgrammePanel initiallyOpen creationEnabled={workflow?.creationEnabled} />
      )}
      {view === 'worlds' && <WorldLibraryPage creationEnabled={workflow?.creationEnabled} />}
      {view === 'films' && <FilmStudioPage embedded embeddedView="library" />}
      {view === 'accounts' && <FilmSocialPanel />}
      {view === 'schedule' && (
        <section className="space-y-5">
          <ReleasePanel />
          <details className="rounded-2xl border bg-card p-5">
            <summary className="cursor-pointer font-medium">
              Advanced: recurring renders from existing worlds
            </summary>
            <div className="mt-5">
              <FilmStudioPage embedded embeddedView="schedules" />
            </div>
          </details>
        </section>
      )}
      {view === 'workflow' && (
        <section className="space-y-5">
          <ProducerContractPanel />
          <div className="rounded-2xl border bg-card p-5 sm:p-6">
            <h2 className="text-xl font-semibold">Default route · source-faithful daily visit</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              The versioned production route used by the current fresh-world runner—not an editable
              promise of unsupported effects or a switch that bypasses review.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                [
                  'Discover',
                  'Curated artist pool → catalogue + Picasso concepts + published bio. Select concepts and approve creation once.',
                ],
                [
                  'Build & screen',
                  'Server-side original checks → panorama → source-matched 3D layers → independent critique → Immersity assets and stored AR reference → world library.',
                ],
                [
                  'Optional film & review',
                  `Portrait 1080×1920 · ${workflow?.durationSec || 46}s · music only · grounded preamble, three callouts, artist ending.`,
                ],
              ].map(([title, text]) => (
                <div key={title} className="rounded-xl border bg-muted/30 p-4">
                  <h3 className="font-semibold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Current music is procedural atmospheric synthesis, not bespoke generated music. Fresh
              residency films remain a later route.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border bg-card p-5">
              <h3 className="font-semibold">What is enabled</h3>
              <dl className="mt-4 space-y-3 text-sm">
                {[
                  [
                    'Weekly idea discovery',
                    workflow ? (workflow.discoveryEnabled ? 'Enabled' : 'Paused') : 'Unknown',
                  ],
                  [
                    'Fresh-world production',
                    workflow ? (workflow.creationEnabled ? 'Enabled' : 'Paused') : 'Unknown',
                  ],
                  [
                    'World spend contract configured',
                    workflow
                      ? workflow.costContractConfigured
                        ? 'Configured; producer attestation still enforced per run'
                        : 'Not configured'
                      : 'Unknown',
                  ],
                  [
                    'Immersity commercial allowance',
                    workflow?.immersityPricingConfigured
                      ? 'Configured; credentials and funded credits still required'
                      : 'Not priced — world previews can finish, ancillary completion stays held',
                  ],
                  [
                    'Instagram publishing',
                    workflow?.publicationEnabled ? 'Exact-release approval required' : 'Paused',
                  ],
                ].map(([label, value]) => (
                  <div key={label} className="flex flex-wrap justify-between gap-2 border-b pb-3">
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="max-w-sm font-medium">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="rounded-2xl border bg-card p-5">
              <h3 className="font-semibold">Your launch controls</h3>
              <ul className="mt-4 space-y-3 text-sm">
                {[
                  'Select artwork concepts and approve managed creation within the shared weekly budget.',
                  'Browse built worlds and open desktop/mobile QR previews; choose which worlds become films.',
                  'Watch and approve the final encoded film after independent critique.',
                  'Approve a release bound to the connected account, Reel/Story format and exact posting time.',
                ].map((text) => (
                  <li key={text} className="flex gap-2">
                    <CheckCircle2
                      className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    {text}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                Shared weekly reservation limit: $
                {((workflow?.weeklyReservationCapCents || 2500) / 100).toFixed(2)}. Reservations are
                not invoices or a provider hard dollar cap; cloud/storage charges are separate.
                Unknown charges retain their holds.
              </p>
            </div>
          </div>
          <div className="rounded-2xl border bg-muted/30 p-5">
            <h3 className="font-semibold">Automation ramp</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Next: accept the producer spend policy and validate a small live batch through the
              exact-film release queue. Later, allow bounded automatic idea approval and scheduling
              only after measured quality, cost, rights and account checks pass. No autonomous
              posting is activated by this redesign.
            </p>
          </div>
        </section>
      )}
      <Dialog
        open={!!cancelRun}
        onOpenChange={(open) => {
          if (!open && !busy) setCancelRun(null)
        }}
      >
        <DialogContent className="max-h-[90dvh] overflow-y-auto font-sans">
          <DialogHeader>
            <DialogTitle>Cancel this production run?</DialogTitle>
            <DialogDescription>
              Stop the artwork → world → film pipeline for {cancelRun?.title}. Work already
              dispatched may finish or require reconciliation. Cancellation does not erase artifacts
              or refund charges; unknown costs retain their holds. No customer scene or Instagram
              post is changed.
            </DialogDescription>
          </DialogHeader>
          {message && (
            <p role="status" className="text-sm">
              {message}
            </p>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setCancelRun(null)}>
              Keep run
            </Button>
            <Button
              variant="destructive"
              disabled={busy || !cancelRun}
              onClick={async () => {
                if (!cancelRun || busy) return
                setBusy(true)
                try {
                  await apiClient.post(`/api/v1/worlds/creations/${cancelRun.id}/cancel`)
                  setCancelRun(null)
                  setMessage(
                    'Production cancellation recorded. Dispatched work and costs may still require reconciliation.'
                  )
                  await queryClient.invalidateQueries({ queryKey: ['content-studio'] })
                  await queryClient.invalidateQueries({ queryKey: ['world-creations'] })
                } catch (e) {
                  const error = e as { response?: { data?: { error?: string } } }
                  setMessage(
                    error.response?.data?.error ||
                      'Cancellation could not be confirmed. Refresh the run before retrying.'
                  )
                } finally {
                  setBusy(false)
                }
              }}
            >
              {busy ? 'Cancelling…' : 'Confirm cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={discoverOpen}
        onOpenChange={(open) => {
          if (!busy) setDiscoverOpen(open)
        }}
      >
        <DialogContent className="max-h-[90dvh] overflow-y-auto font-sans">
          <DialogHeader>
            <DialogTitle>Plan a week of artwork ideas</DialogTitle>
            <DialogDescription>
              The system selects from your curated artist pool and brings back grounded ideas for
              review. This does not create worlds or post anything.
            </DialogDescription>
          </DialogHeader>
          <label className="text-sm font-medium">
            Week beginning (Monday, Chicago)
            <input
              aria-label="Plan week beginning"
              className="mt-2 min-h-11 w-full rounded-lg border bg-background p-3"
              type="date"
              value={week}
              onChange={(e) => {
                setWeek(e.target.value)
                confirmed.current = false
              }}
            />
          </label>
          <p className="rounded-xl bg-muted p-4 text-sm leading-relaxed">
            Next, review the artwork originals in Ideas. Selected sources can enter the default
            world → film route only after rights and budget approval. Repeating the same week reuses
            its durable discovery run; it does not invent a new paid attempt.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setDiscoverOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={busy || !week || !workflow?.discoveryEnabled}
              onClick={async () => {
                if (confirmed.current) return
                confirmed.current = true
                setBusy(true)
                setMessage('')
                try {
                  const { data: result } = await apiClient.post<{ id: string; status: string }>(
                    '/api/v1/films/programmes/discovery/runs',
                    { weekStart: week }
                  )
                  setDiscoverOpen(false)
                  void queryClient.invalidateQueries({ queryKey: ['film-discovery'] })
                  void queryClient.invalidateQueries({ queryKey: ['content-studio'] })
                  setParams({ view: 'ideas' })
                  setMessage(
                    `Discovery run ${result.status}. No world generation or publication was approved.`
                  )
                } catch (e) {
                  confirmed.current = false
                  const error = e as { response?: { data?: { error?: string } } }
                  setMessage(
                    error.response?.data?.error ||
                      'Discovery could not start. Check the configured artist pool and week.'
                  )
                } finally {
                  setBusy(false)
                }
              }}
            >
              {busy ? 'Planning…' : 'Discover ideas'}
            </Button>
          </DialogFooter>
          {message && (
            <p role="status" className="text-sm text-destructive">
              {message}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </main>
  )
}
